import {
  Point as CameraPoint,
  PresenceZone,
  PresenceZoneRequest,
} from '@hakimo-ui/hakimo/types';
import {
  cameraZoneToCanvasZone,
  canvasZoneToCameraPoints,
  CanvasDimensions,
  ImageResolution,
  SpecialZoneType,
  ZonePolygonData,
} from '@hakimo-ui/hakimo/ui-elements';

/**
 * Server zones as canvas polygons.
 *
 * A zone's polygon lives in its own labellingResolution space, which is the
 * image size when it was drawn here — but a zone seeded by the backend tool
 * may carry none, in which case the backend treats it as frame coordinates
 * and so do we, taking the current image as the frame. The whole-camera
 * zone has no polygon and is not drawn.
 */
export function presenceZonesToCanvas(
  zones: PresenceZone[],
  imageResolution: ImageResolution,
  canvasDimensions: CanvasDimensions
): ZonePolygonData[] {
  return zones
    .filter((zone) => !zone.isDefault && zone.polygon)
    .map((zone) => {
      const resolution: ImageResolution = zone.labellingResolution
        ? {
            width: zone.labellingResolution[0],
            height: zone.labellingResolution[1],
          }
        : imageResolution;
      return {
        ...cameraZoneToCanvasZone(
          zone.polygon as CameraPoint[],
          zone.id,
          SpecialZoneType.PresenceZone,
          resolution,
          canvasDimensions
        ),
        label: zone.name,
      };
    });
}

/** A canvas polygon as the request that creates or redraws it. */
export function canvasZoneToRequest(
  zone: ZonePolygonData,
  name: string,
  canvasDimensions: CanvasDimensions,
  imageResolution: ImageResolution
): PresenceZoneRequest {
  const polygon = canvasZoneToCameraPoints(
    zone,
    canvasDimensions,
    imageResolution
  ).map(([x, y]) => [Math.round(x), Math.round(y)] as CameraPoint);
  return {
    name: name.trim(),
    polygon,
    labellingResolution: [imageResolution.width, imageResolution.height],
  };
}

export function samePoints(a: ZonePolygonData, b: ZonePolygonData): boolean {
  if (a.points.length !== b.points.length) return false;
  return a.points.every(
    (p, i) =>
      Math.abs(p.x - b.points[i].x) < 0.5 && Math.abs(p.y - b.points[i].y) < 0.5
  );
}
